import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { Effect } from 'effect';

import type { BackendApiErrorUnion } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import type { CustomerRegisterPayload } from '@vs-shop/schemas/customer';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/customers';
const HTTP_METHOD = 'post';

export const CreateCustomerError = createBackendApiError({
  domain: 'Customer',
  endpoint: 'Create',
  path: ENDPOINT,
  identifierField: 'email',
} as const);

export type CustomerCreateError = BackendApiErrorUnion<typeof CreateCustomerError>;

const postCustomerFetch = (data: CustomerRegisterPayload) =>
  Effect.gen(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/json', 'application/json')
      .fetch({ body: data });
  }).pipe(
    Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
      kind: 'client',
      attributes: {
        'http.request.method': HTTP_METHOD,
        'customer.email': data.email,
      },
    })
  );

export const createCustomer = (data: CustomerRegisterPayload) =>
  postCustomerFetch(data).pipe(
    Effect.map((response) => response.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.flatMap(
      HttpClientResponse.matchStatus({
        '2xx': () => Effect.void,
        404: (response) =>
          Effect.fail(new CreateCustomerError.NotFoundError({ email: data.email, response })),
        422: CreateCustomerError.createViolationError(data.email),
        orElse: (response) =>
          Effect.fail(new CreateCustomerError.ApiRequestError({ email: data.email, response })),
      })
    ),
    Effect.tap(() =>
      logApiOperation(
        ApiModule.CustomerCreate,
        'Debug',
        `Successfully created customer with email: ${data.email}`
      )
    ),
    Effect.withSpan('api.customer.create', {
      kind: 'internal',
      attributes: {
        'customer.email': data.email,
      },
    })
  );
